import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StorePack } from "../../../entity/StorePack";
import WSPayment from "../../../services/remote/payment/WSPayment";
import "./PaymentManager.css";

const EMPTY_FORM: Omit<StorePack, "id" | "createdAt" | "updatedAt"> = {
  name: "",
  price: 0,
  description: "",
  features: [],
  urlRedirect: "",
};

export const PaymentManager = () => {
  const [packs, setPacks] = useState<StorePack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editingPack, setEditingPack] = useState<StorePack | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [featuresInput, setFeaturesInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { t } = useTranslation();

  const loadPacks = async () => {
    setLoading(true);
    try {
      const data = await WSPayment.getInstance().getStorePacks();
      setPacks(data);
    } catch {
      setError(t("paymentManager.errorLoad"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPacks();
  }, []);

  const openCreate = () => {
    setEditingPack(null);
    setForm(EMPTY_FORM);
    setFeaturesInput("");
    setError(null);
    setShowModal(true);
  };

  const openEdit = (pack: StorePack) => {
    setEditingPack(pack);
    setForm({
      name: pack.name,
      price: pack.price,
      description: pack.description,
      features: pack.features,
      urlRedirect: pack.urlRedirect,
    });
    setFeaturesInput(pack.features.join(", "));
    setError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPack(null);
    setError(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError(t("paymentManager.errorFields"));
      return;
    }
    const payload = {
      ...form,
      features: featuresInput
        .split(",")
        .map((f) => f.trim())
        .filter(Boolean),
    };
    setSaving(true);
    setError(null);
    try {
      if (editingPack) {
        await WSPayment.getInstance().update({
          ...payload,
          id: editingPack.id,
        });
        setPacks((prev) =>
          prev.map((p) => (p.id === editingPack.id ? payload : p)),
        );
      } else {
        const created = await WSPayment.getInstance().createPack(payload);
        setPacks((prev) => [{ ...payload, id: created }, ...prev]);
      }
      closeModal();
    } catch {
      setError(
        editingPack
          ? t("paymentManager.errorUpdate")
          : t("paymentManager.errorCreate"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await WSPayment.getInstance().delete(id);
      setPacks((prev) => prev.filter((p) => p.id !== id));
    } catch {
      setError(t("paymentManager.errorDelete"));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="pm-root">
      {/* Header */}
      <div className="pm-header">
        <div>
          <div className="pm-eyebrow">💳 {t("paymentManager.title")}</div>
          <h1>{t("paymentManager.heading")}</h1>
          <p>{t("paymentManager.subheading")}</p>
        </div>
        <button className="pm-btn-primary" onClick={openCreate}>
          + {t("paymentManager.addPack")}
        </button>
      </div>

      {/* Error banner */}
      {error && !showModal && (
        <div className="pm-error">
          {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Stats */}
      <div className="pm-stats">
        <div className="pm-stat-card">
          <span className="pm-stat-label">
            {t("paymentManager.totalPacks")}
          </span>
          <span className="pm-stat-value">{packs.length}</span>
        </div>
        <div className="pm-stat-card">
          <span className="pm-stat-label">
            {t("paymentManager.lowestPrice")}
          </span>
          <span className="pm-stat-value">
            {packs.length ? `$${Math.min(...packs.map((p) => p.price))}` : "—"}
          </span>
        </div>
        <div className="pm-stat-card">
          <span className="pm-stat-label">
            {t("paymentManager.highestPrice")}
          </span>
          <span className="pm-stat-value">
            {packs.length ? `$${Math.max(...packs.map((p) => p.price))}` : "—"}
          </span>
        </div>
      </div>

      <hr className="pm-divider" />

      {/* Table */}
      <div className="pm-table-section">
        <div className="pm-table-header">
          <h2>{t("paymentManager.list")}</h2>
          <span className="pm-count">
            {packs.length} {t("paymentManager.packs")}
          </span>
        </div>

        <div className="pm-table-wrap">
          <table className="pm-table">
            <thead>
              <tr>
                <th style={{ width: "22%" }}>{t("paymentManager.name")}</th>
                <th style={{ width: "10%" }}>{t("paymentManager.price")}</th>
                <th style={{ width: "28%" }}>
                  {t("paymentManager.description")}
                </th>
                <th style={{ width: "22%" }}>{t("paymentManager.features")}</th>
                <th style={{ width: "12%" }}>
                  {t("paymentManager.createdAt")}
                </th>
                <th style={{ width: "6%" }} />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i} className="pm-skel-row">
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j}>
                        <div
                          className="pm-skel-block"
                          style={{ height: 12, width: j === 5 ? 40 : "70%" }}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              ) : packs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="pm-empty">
                    {t("paymentManager.empty")}
                  </td>
                </tr>
              ) : (
                packs.map((pack) => (
                  <tr key={pack.id}>
                    <td className="pm-name-cell">{pack.name}</td>
                    <td className="pm-price-cell">${pack.price}</td>
                    <td className="pm-desc-cell">{pack.description}</td>
                    <td>
                      <div className="pm-features-wrap">
                        {pack.features.slice(0, 3).map((f) => (
                          <span key={f} className="pm-feature-tag">
                            {f}
                          </span>
                        ))}
                        {pack.features.length > 3 && (
                          <span className="pm-feature-more">
                            +{pack.features.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="td-muted">
                      {pack.createdAt
                        ? new Date(pack.createdAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td>
                      <div className="pm-actions">
                        <button
                          className="pm-btn-icon"
                          onClick={() => openEdit(pack)}
                          title={t("paymentManager.edit")}
                        >
                          ✎
                        </button>
                        <button
                          className="pm-btn-icon delete"
                          onClick={() => handleDelete(pack!.id!)}
                          disabled={deletingId === pack!.id}
                          title={t("paymentManager.delete")}
                        >
                          {deletingId === pack!.id ? "…" : "✕"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="pm-backdrop" onClick={closeModal}>
          <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h2>
                {editingPack
                  ? t("paymentManager.editPack")
                  : t("paymentManager.addPack")}
              </h2>
              <button onClick={closeModal}>✕</button>
            </div>

            {error && <div className="pm-error modal">{error}</div>}

            <div className="pm-form">
              <label>{t("paymentManager.name")}</label>
              <input
                type="text"
                placeholder="Pro Plan"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />

              <label>{t("paymentManager.price")}</label>
              <input
                type="number"
                min={0}
                placeholder="49"
                value={form.price}
                onChange={(e) =>
                  setForm({ ...form, price: Number(e.target.value) })
                }
              />

              <label>{t("paymentManager.description")}</label>
              <textarea
                rows={3}
                placeholder={t("paymentManager.descriptionPlaceholder")}
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />

              <label>
                {t("paymentManager.features")}{" "}
                <span className="pm-label-hint">
                  ({t("paymentManager.featuresHint")})
                </span>
              </label>
              <input
                type="text"
                placeholder="Unlimited stores, Analytics, Priority support"
                value={featuresInput}
                onChange={(e) => setFeaturesInput(e.target.value)}
              />

              <label>{t("paymentManager.urlRedirect")}</label>
              <input
                type="url"
                placeholder="https://checkout.stripe.com/..."
                value={form.urlRedirect}
                onChange={(e) =>
                  setForm({ ...form, urlRedirect: e.target.value })
                }
              />
            </div>

            <div className="pm-modal-footer">
              <button className="pm-btn-secondary" onClick={closeModal}>
                {t("paymentManager.cancel")}
              </button>
              <button
                className="pm-btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving
                  ? t("paymentManager.saving")
                  : editingPack
                    ? t("paymentManager.saveChanges")
                    : t("paymentManager.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
